import {Button, openConfirmDialog, openDialog} from 'platform/components';
import {Heading, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {mergeAll} from 'ramda';

import {
  usePostStorageLocationDeleteApiMutation,
  usePostStorageLocationSetAsActiveApiMutation,
  usePostStorageLocationSetAsInactiveApiMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {
  catchUnhandledDataGridActions,
  CreateTireLocation,
  DmsDataGrid,
  handleApiError,
  refreshDmsDatagrid,
} from '@dms/shared';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {CreateMultipleLocations} from './CreateMultipleLocations';
import {NotesDialog} from './NotesDialog';
import {SingleLocationDetail} from './SingleLocationDetail';

const PREDEFINED_COLUMNS = ['note'] as const;

interface StorageLocationsProps {
  tireWarehouseId: string;
}

export function StorageLocations(props: StorageLocationsProps) {
  const [deleteStorageLocation] = usePostStorageLocationDeleteApiMutation();
  const [setAsInactive] = usePostStorageLocationSetAsInactiveApiMutation();
  const [setAsActive] = usePostStorageLocationSetAsActiveApiMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {tireWarehouse: props.tireWarehouseId}]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (params) => {
    const storageLocationIds = Array.isArray(params.rowId) ? params.rowId : [params.rowId];

    match(params.actionKey)
      .with('note', () => {
        // A row without a note has a nil cell — open the dialog empty in that case.
        const noteData = params.getColumnData<string>('note', {isOptional: true})?.value;

        openDialog(<NotesDialog rowIds={storageLocationIds} defaultNote={noteData} />, {
          title: i18n.t('general.actions.addNote'),
          id: 'storage-locations-notes-dialog',
        });
      })
      .with('delete', () => {
        openConfirmDialog({
          onConfirm: () => {
            deleteStorageLocation({
              body: {storageLocationIds},
            })
              .unwrap()
              .then(() => {
                refreshDmsDatagrid('storage-location-tire-warehouse', {deselectAll: true});
              })
              .catch(handleApiError);
          },
        });
      })
      .with('redirectDetail', () => {
        const castedRowData = Array.isArray(params.rowData) ? params.rowData[0] : params.rowData;
        const storageLocationId = castedRowData.id as string;

        openDialog(
          <SingleLocationDetail
            storageLocationId={storageLocationId}
            dialogId="single-location-detail-dialog"
          />,
          {
            title: i18n.t('entity.tireWarehouses.labels.editStorageLocation'),
            id: 'single-location-detail-dialog',
          }
        );
      })
      .with('setAsInactive', () =>
        setAsInactive({
          body: {storageLocationIds},
        })
          .unwrap()
          .then(() => {
            refreshDmsDatagrid('storage-location-tire-warehouse');
          })
          .catch(handleApiError)
      )
      .with('setAsActive', () =>
        setAsActive({
          body: {storageLocationIds},
        })
          .unwrap()
          .then(() => {
            refreshDmsDatagrid('storage-location-tire-warehouse');
          })
          .catch(handleApiError)
      )
      .otherwise(() => catchUnhandledDataGridActions(params.actionKey));
  };

  return (
    <VStack spacing={7}>
      <HStack width="100%" justify="space-between" align="center">
        <Heading size={4}>{i18n.t('entity.tireWarehouses.labels.storageLocations')}</Heading>
        <HStack spacing={2}>
          <Button
            title={i18n.t('entity.tireWarehouses.labels.createSingleLocation')}
            variant="secondary"
            onClick={() =>
              openDialog(
                <CreateTireLocation
                  tireWarehouseId={props.tireWarehouseId}
                  dialogId="create-single-location-dialog"
                  onSuccess={() => refreshDmsDatagrid('storage-location-tire-warehouse')}
                />,
                {
                  title: i18n.t('entity.tireWarehouses.labels.createSingleLocation'),
                  id: 'create-single-location-dialog',
                }
              )
            }
          />
          <Button
            title={i18n.t('entity.tireWarehouses.labels.createMultipleLocation')}
            variant="secondary"
            onClick={() =>
              openDialog(<CreateMultipleLocations tireWarehouseId={props.tireWarehouseId} />, {
                title: i18n.t('entity.tireWarehouses.labels.createMultipleLocation'),
                id: 'create-multiple-locations-dialog',
              })
            }
          />
        </HStack>
      </HStack>
      <DmsDataGrid
        gridCode="storage-location-tire-warehouse"
        queryModifier={queryModifier}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        autoHeight
      />
    </VStack>
  );
}
